import React, { useEffect, useMemo } from "react";
import {
  Stack,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox,
  Box,
} from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { createParent, updateParent } from "../redux/actions/parentActions";
import { getStudents } from "../redux/actions/studentActions";
import { parentActions } from "../redux/slices/parent";
import { cleanFormData } from "../helpers/formHelpers";

import { ParentGuardianResponseDto } from "../shared/dtos/parentGuardian/ParentGuardianResponseDto";
import { ParentGuardianCreateRequestDto } from "../shared/dtos/parentGuardian/ParentGuardianCreateRequestDto";
import { ParentGuardianUpdateRequestDto } from "../shared/dtos/parentGuardian/ParentGuardianUpdateRequestDto";
import { RelationshipType } from "../shared/enums/RelationshipType";

const relationshipOptions = Object.values(RelationshipType).map((r) => ({
  value: r,
  label: r.charAt(0) + r.slice(1).toLowerCase().replace(/_/g, " "),
}));

const addressSchema = Yup.object().shape({
  streetLine1: Yup.string().required("Street Line 1 is required"),
  streetLine2: Yup.string().nullable(),
  city: Yup.string().required("City is required"),
  stateOrProvince: Yup.string().required("State/Province is required"),
  postalCode: Yup.string().required("Postal Code is required"),
  country: Yup.string().required("Country is required"),
});

const baseValidation = {
  primaryPhoneNumber: Yup.string().required("Primary phone is required"),
  secondaryPhoneNumber: Yup.string().nullable(),
  occupation: Yup.string().nullable(),
  address: addressSchema,
};

const createParentSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  middleName: Yup.string().trim().nullable(),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  studentId: Yup.string().required("Student selection is required"),
  relationshipType: Yup.mixed<RelationshipType>()
    .oneOf(Object.values(RelationshipType))
    .required("Relationship type is required"),
  isPrimaryContact: Yup.boolean().required(),
  canPickup: Yup.boolean().required(),
  ...baseValidation,
});

const updateParentSchema = Yup.object().shape({
  ...baseValidation,
});

interface CreateOrUpdateParentFormProps {
  parent?: ParentGuardianResponseDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateParentForm: React.FC<CreateOrUpdateParentFormProps> = ({
  parent = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();
  const { items: students } = useAppSelector((state) => state.student);

  useEffect(() => {
    if (!isEditing && (!students || students.length === 0)) {
      dispatch(getStudents());
    }
  }, [dispatch, isEditing, students]);

  const studentOptions = useMemo(
    () =>
      students?.map((s) => ({
        value: s.id,
        label: `${s.firstName} ${s.lastName} (${s.studentId || "No ID"})`,
      })) || [],
    [students],
  );

  const initialValues = {
    firstName: parent?.firstName || "",
    middleName: parent?.middleName || "",
    lastName: parent?.lastName || "",
    email: parent?.email || "",
    primaryPhoneNumber: parent?.primaryPhoneNumber || "",
    secondaryPhoneNumber: "",
    occupation: parent?.occupation || "",
    address: {
      streetLine1: parent?.address?.streetLine1 || "",
      streetLine2: parent?.address?.streetLine2 || "",
      city: parent?.address?.city || "",
      stateOrProvince: parent?.address?.stateOrProvince || "",
      postalCode: parent?.address?.postalCode || "",
      country: parent?.address?.country || "Kenya",
    },
    studentId: "",
    relationshipType: RelationshipType.MOTHER,
    isPrimaryContact: true,
    canPickup: true,
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(parentActions.setErrorUpdate(null))
            : () => dispatch(parentActions.setErrorCreate(null))
        }
      />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={isEditing ? updateParentSchema : createParentSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;

          if (isEditing && parent) {
            const updatePayload: ParentGuardianUpdateRequestDto = {
              primaryPhoneNumber: rawCleaned.primaryPhoneNumber,
              secondaryPhoneNumber: rawCleaned.secondaryPhoneNumber || null,
              occupation: rawCleaned.occupation || null,
              address: {
                streetLine1: rawCleaned.address.streetLine1,
                streetLine2: rawCleaned.address.streetLine2 || null,
                city: rawCleaned.address.city,
                stateOrProvince: rawCleaned.address.stateOrProvince,
                postalCode: rawCleaned.address.postalCode,
                country: rawCleaned.address.country,
              },
            };
            dispatch(updateParent({ id: parent.id, values: updatePayload }));
          } else {
            const createPayload: ParentGuardianCreateRequestDto = {
              firstName: rawCleaned.firstName,
              middleName: rawCleaned.middleName,
              lastName: rawCleaned.lastName,
              email: rawCleaned.email,
              primaryPhoneNumber: rawCleaned.primaryPhoneNumber,
              secondaryPhoneNumber: rawCleaned.secondaryPhoneNumber || null,
              occupation: rawCleaned.occupation || null,
              address: {
                streetLine1: rawCleaned.address.streetLine1,
                streetLine2: rawCleaned.address.streetLine2 || null,
                city: rawCleaned.address.city,
                stateOrProvince: rawCleaned.address.stateOrProvince,
                postalCode: rawCleaned.address.postalCode,
                country: rawCleaned.address.country,
              },
              studentId: rawCleaned.studentId,
              relationshipType: values.relationshipType,
              isPrimaryContact: Boolean(values.isPrimaryContact),
              canPickup: Boolean(values.canPickup),
            };
            dispatch(createParent(createPayload));
          }
        }}
      >
        {({
          dirty,
          isValid,
          values,
          handleChange,
        }: FormikProps<typeof initialValues>) => (
          <Form>
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "text.secondary",
                    textAlign: "left",
                    fontWeight: 700,
                  }}
                >
                  Personal Details
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <FormTextField
                    name="firstName"
                    label="First Name"
                    disabled={isEditing}
                  />
                  <FormTextField
                    name="middleName"
                    label="Middle Name (Optional)"
                    disabled={isEditing}
                  />
                  <FormTextField
                    name="lastName"
                    label="Last Name"
                    disabled={isEditing}
                  />
                </Box>

                <FormTextField
                  name="email"
                  label="Email Address"
                  type="email"
                  disabled={isEditing}
                />
                <Divider sx={{ my: 1 }} />
              </Box>

              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                  textAlign: "left",
                  fontWeight: 700,
                }}
              >
                Contact & Profession
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="primaryPhoneNumber"
                  label="Primary Phone"
                />
                <FormTextField
                  name="secondaryPhoneNumber"
                  label="Secondary Phone (Optional)"
                />
              </Box>

              <FormTextField
                name="occupation"
                label="Occupation (e.g., Engineer, Doctor)"
              />

              <Divider sx={{ my: 1 }} />

              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                  textAlign: "left",
                  fontWeight: 700,
                }}
              >
                Address Information
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="address.streetLine1"
                  label="Street Line 1"
                />
                <FormTextField
                  name="address.streetLine2"
                  label="Street Line 2 (Optional)"
                />
                <FormTextField name="address.city" label="City" />
                <FormTextField
                  name="address.stateOrProvince"
                  label="State/Province"
                />
                <FormTextField name="address.postalCode" label="Postal Code" />
                <FormTextField name="address.country" label="Country" />
              </Box>
              <Divider sx={{ my: 1 }} />

              {!isEditing && (
                <Box>
                  <Stack>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "text.secondary",
                        textAlign: "left",
                        fontWeight: 700,
                      }}
                    >
                      Link to Student
                    </Typography>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 2,
                      }}
                    >
                      <FormSelectField
                        name="studentId"
                        label="Select Student"
                        options={studentOptions}
                      />

                      <FormSelectField
                        name="relationshipType"
                        label="Relationship"
                        options={relationshipOptions}
                      />
                    </Box>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={3}
                    sx={{ justifyContent: "flex-start" }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.isPrimaryContact}
                          onChange={handleChange}
                          name="isPrimaryContact"
                        />
                      }
                      label="Primary Contact"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.canPickup}
                          onChange={handleChange}
                          name="canPickup"
                        />
                      }
                      label="Authorized to Pickup"
                    />
                  </Stack>
                </Box>
              )}

              <SubmitButton
                title={isEditing ? "Save Changes" : "Register Parent"}
                loadingText={isEditing ? "Saving..." : "Registering..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <PersonAddOutlinedIcon />
                }
                sx={{ mt: 2 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateOrUpdateParentForm;
